import { Stack } from '@mui/material';
import { useState } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button';
import { Grid } from '@/components/layout/Grid';
import { HeaderCard } from '@/components/layout/HeaderCard';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { ClientImportExport } from '@/pages/internal/DevToolsPage/ClientImportExport/ClientImportExport';
import { TenantOrgMembershipType, TenantUsageBucket } from '@/types/schema';

import { CreateTenantModal } from './CreateTenantModal/CreateTenantModal';
import {
  Operations,
  useTenantSettingsQuery,
  useUpdateTenantMutation,
} from './graphql/AdminTenantsPage.generated';
import { PendingDeletionConfirmationModal } from './PendingDeletionConfirmationModal/PendingDeletionConfirmationModal';
import { TenantSettingsForm } from './TenantSettingsForm/TenantSettingsForm';

export function AdminTenantsPage() {
  const { showFeedback } = useFeedback();
  const { isTenantInformationLoaded, orgMembershipType } =
    useTenantDetailsContext();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPendingDeletionModalOpen, setIsPendingDeletionModalOpen] =
    useState(false);

  const { data: tenantData } = useTenantSettingsQuery();
  const [updateTenant] = useUpdateTenantMutation({
    onCompleted: () => {
      setIsPendingDeletionModalOpen(false);
      showFeedback('Tenant deletion status updated', {
        variant: 'success',
      });
    },
    onError: () => {
      showFeedback('Failed to update tenant deletion status');
    },
  });

  const isMarkedForDeletion =
    tenantData?.tenantInformation?.usageBucket ===
    TenantUsageBucket.PendingDeletion;
  const isMemberTenant = orgMembershipType === TenantOrgMembershipType.Member;
  const canCreateTenant = isTenantInformationLoaded && !isMemberTenant;

  const handlePendingDeletionConfirm = async (
    nextUsageBucket: TenantUsageBucket
  ) => {
    if (
      !tenantData?.tenantInformation?.id ||
      !tenantData?.tenantInformation?.branding?.id
    ) {
      return;
    }

    return updateTenant({
      variables: {
        tenantInput: {
          id: tenantData.tenantInformation.id,
          update: {
            usageBucket: nextUsageBucket,
          },
        },
        brandingId: tenantData.tenantInformation.branding.id,
        brandingInput: {}, // Empty branding input since we're not updating branding
      },
      refetchQueries: [Operations.Query.TenantSettings],
    });
  };

  return (
    <SubpageLayout heading="Tenants">
      <Grid container spacing={2}>
        <Grid item lg={6} sm={8}>
          <Stack spacing={2}>
            <HeaderCard heading="Tenant tools">
              <Stack spacing={2}>
                {isMarkedForDeletion && (
                  <Callout severity="error">
                    WARNING: THIS TENANT IS MARKED FOR DELETION.
                  </Callout>
                )}
                <Tooltip
                  title="Tenants can only be created through a standalone tenant or an organization's parent tenant."
                  show={isMemberTenant}
                >
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!canCreateTenant}
                    onClick={() => setIsCreateModalOpen(true)}
                  >
                    Create new tenant
                  </Button>
                </Tooltip>
                <Button
                  variant={isMarkedForDeletion ? 'secondary' : 'primary'}
                  size="sm"
                  onClick={() => setIsPendingDeletionModalOpen(true)}
                >
                  {isMarkedForDeletion
                    ? 'Remove from deletion queue'
                    : 'Mark this tenant for deletion'}
                </Button>
              </Stack>
            </HeaderCard>
            <ClientImportExport />
          </Stack>
        </Grid>
        <Grid item lg={6} sm={8}>
          <HeaderCard heading="Current tenant configuration">
            <TenantSettingsForm />
          </HeaderCard>
        </Grid>
      </Grid>

      {isCreateModalOpen && (
        <CreateTenantModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}

      <PendingDeletionConfirmationModal
        isOpen={isPendingDeletionModalOpen}
        onClose={() => setIsPendingDeletionModalOpen(false)}
        onConfirm={handlePendingDeletionConfirm}
        isMarkedForDeletion={isMarkedForDeletion}
      />
    </SubpageLayout>
  );
}
